import { IconButton } from "@shopflo/ui/icon-button";
import { Minus, Plus, Trash } from "@phosphor-icons/react";
import { classNames } from "lib/utils/helpers";
import React, { useEffect, useRef, useState } from "react";
import { getTextColorByBrightness } from "@shopflo/ui/utils";

const MIN_QUANTITY = 1;

interface QuantitySelectorProps {
  quantity: number;
  isCartItem: boolean;
  isDecrementDisabled: boolean;
  isIncrementDisabled: boolean;
  commonDisabledRules?: boolean;
  onIncrement?: (event: React.MouseEvent) => void;
  onDecrement?: (event: React.MouseEvent) => void;
  /** When passed, the decrement button turns into a delete action at the minimum quantity. */
  onDelete?: (event: React.MouseEvent) => void;
  /** Upper bound for manual entry — typically min(inventory quantity, merchant max per item) */
  maxQuantity?: number;
  /** When passed, the quantity becomes a text input. Fires on blur/Enter with the clamped value. */
  onQuantityChange?: (quantity: number) => void;
  /** Row-aligned line items fold delete into the decrement button instead of a separate action. */
  isRowAligned?: boolean;
  customisations?: {
    quantityEditorBackground: string;
    quantityEditorText: string;
  };
  showQuantityEditor?: boolean;
}

export const QuantitySelector: React.FC<QuantitySelectorProps> = ({
  quantity,
  isCartItem,
  isDecrementDisabled,
  isIncrementDisabled,
  commonDisabledRules,
  onIncrement,
  onDecrement,
  onDelete,
  maxQuantity,
  onQuantityChange,
  customisations,
  isRowAligned = false,
  showQuantityEditor = true
}) => {
  // At the minimum quantity the decrement button reads as "remove the item", so it shows a trash
  // and deletes instead of decrementing (which the parent disables at MIN_QUANTITY anyway).
  const showDeleteIcon = isRowAligned && quantity === MIN_QUANTITY && Boolean(onDelete);
  const isEditable = Boolean(onQuantityChange) && !Boolean(commonDisabledRules) && showQuantityEditor;
  const [draft, setDraft] = useState<string>(String(quantity));
  const inputRef = useRef<HTMLInputElement>(null);

  // the server is the source of truth — resync whenever it hands us a new quantity
  useEffect(() => {
    setDraft(String(quantity));
  }, [quantity]);

  const commitDraft = () => {
    const parsed = parseInt(draft, 10);
    if (isNaN(parsed)) {
      setDraft(String(quantity));
      return;
    }
    const upperBound = maxQuantity && maxQuantity > 0 ? maxQuantity : parsed;
    const clamped = Math.max(MIN_QUANTITY, Math.min(parsed, upperBound));
    setDraft(String(clamped));
    if (clamped === quantity) return;
    onQuantityChange?.(clamped);
  };

  const textStyle = {
    color: customisations?.quantityEditorText,
  };

  const maxDigits = maxQuantity && maxQuantity > 0 ? String(maxQuantity).length : 4;
  const digitWidth = `${Math.min(Math.max(draft.length, 2), maxDigits)}ch`;

  return (
    <div
      className={classNames(
        "flex  h-7 p-0 min-w-[72px] flex-row items-center justify-between rounded border overflow-hidden",
        isCartItem ? "border-carbon-lighter bg-white" : "border-gray-light",
      )}
      style={{
        backgroundColor: customisations?.quantityEditorBackground,
        borderColor: getTextColorByBrightness({ bgHexColor: customisations?.quantityEditorBackground ?? '#FFFFFF', lightOpacity: 24 }),
      }}
    >
      <IconButton
        disabled={showDeleteIcon ? commonDisabledRules : isDecrementDisabled || commonDisabledRules}
        IconComponent={showDeleteIcon ? Trash : Minus}
        variant="ghost"
        className="h-full w-6 shrink-0"
        size="xxs"
        iconProps={{
          weight: "bold",
          className: classNames("text-zinc-500", showDeleteIcon ? "h-3.5 w-3.5" : ""),
          color: customisations?.quantityEditorText,
          opacity: '60%',
        }}
        onClick={showDeleteIcon ? onDelete : onDecrement}
      />

      {isEditable ? (
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={draft}
          maxLength={maxDigits}
          className="shrink-0 bg-transparent p-0 text-center text-xs font-medium text-coal-dark outline-none focus:outline-none"
          style={{ ...textStyle, width: digitWidth }}
          onChange={(event) => setDraft(event.target.value.replace(/\D/g, "").slice(0, maxDigits))}
          onClick={(event) => event.stopPropagation()}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              inputRef.current?.blur();
            }
            if (event.key === "Escape") {
              setDraft(String(quantity));
              inputRef.current?.blur();
            }
          }}
        />
      ) : (
        <p
          className="shrink-0 text-center text-xs font-medium text-coal-dark"
          style={{ ...textStyle, width: `${Math.min(Math.max(String(quantity).length, 2), maxDigits)}ch` }}>
          {quantity}
        </p>
      )}
      <IconButton
        disabled={isIncrementDisabled || commonDisabledRules}
        IconComponent={Plus}
        className="h-full w-6 shrink-0"
        variant="ghost"
        size="xxs"
        iconProps={{
          weight: "bold",
          className: "text-zinc-500",
          color: customisations?.quantityEditorText,
          opacity: '60%',
        }}
        onClick={onIncrement}
      />
    </div>
  );
};
